/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */

// React Dependencies
import React, { useEffect, useState, useRef } from 'react';

// External Dependencies
import PropTypes from 'prop-types';
import {
  Paper,
  IconButton,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Menu,
  MenuItem,
  Radio,
  RadioGroup,
  FormControlLabel,
} from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import { Add, ArrowBack, DeleteOutline, Edit, Menu as MenuIcon } from '@material-ui/icons';
import { connect } from 'react-redux';
import { toast } from 'react-toastify';

// Internal Dependencies
import MText from '../../../components/Common/MText';
import { containerHeaderTextStyle } from '../../../styles/common';
import Colours from '../../../styles/colours';
import MButton from '../../../components/MButton';
import ApiService from '../../../services/apiService';
import ProgressiveImage from '../../../components/ProgressiveImage';
import { imageResizeUrl } from '../../../common/convert';
import { IsEmpty } from '../../../common/checks';
import ToolTip from '../../../components/Common/ToolTip';
import Icons from '../../../components/Common/Icons';
import PhotoDetailsModal from './PhotoDetailsModal';
import CreateEditAlbumModal from './AlbumsComs/CreateEditAlbumModal';
import { Url, Status } from '../../../common/constants';

const formatAlbumPhoto = (photo, page) => ({
  ...photo,
  display:  photo.cropUrl || photo.url,
  original: photo.url,
  username: page?.name,
  pageLogo: page?.logo,
  info:     {
    ...photo.info,
    location: {
      lat: photo.lat,
      lng: photo.lng,
    },
  },
});

const AlbumDetails = (props) => {
  const { match } = props;
  const albumIdFromUrl = match?.params?.albumId;
  const containerRef = useRef(null);

  // Details state
  const [album, setAlbum] = useState(null);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingAlbum, setEditingAlbum] = useState(null);
  const [albumToDelete, setAlbumToDelete] = useState(null);

  // Menu and Status state
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState(Status.Draft);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  const isReadOnly = IsEmpty(album) || album.status === Status.Rejected;

  const fetchAlbumDetails = async (albumId) => {
    setLoadingDetails(true);
    try {
      const result = await ApiService.getPageAlbum(props.selectedPage.id, albumId);
      setAlbum({
        ...result,
        photos: (result.photos || []).map((photo) => formatAlbumPhoto(photo, props.selectedPage)),
      });
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoadingDetails(false);
    }
  };

  useEffect(() => {
    if (!IsEmpty(albumIdFromUrl)) {
      fetchAlbumDetails(albumIdFromUrl);
    }
  }, [albumIdFromUrl]);

  const handleDeleteAlbum = async () => {
    try {
      await ApiService.deletePageAlbum(props.selectedPage.id, albumToDelete.id);
      toast.success('Album deleted successfully');
      setAlbumToDelete(null);
      props.history.push(Url.Albums.replace(':pageId', props.selectedPage.id));
    } catch (error) {
      toast.error(error.message);
    }
  };

  const onUpdateStatus = async () => {
    try {
      setIsUpdatingStatus(true);
      await ApiService.updatePageAlbum(props.selectedPage.id, album.id, {
        name:        album.name,
        description: album.description,
        status:      selectedStatus,
      });
      toast.success('Album status updated');
      setStatusModalOpen(false);
      fetchAlbumDetails(album.id);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const renderStatusOption = (status, title, description) => (
    <FormControlLabel
      value={status}
      control={(
        <Radio
          style={{
            color:   selectedStatus === status ? Colours.Black : Colours.LightGray4,
            padding: 8,
          }}
        />
      )}
      label={(
        <div className="d-flex flex-column ml-1">
          <MText text={title} bold size={13} />
          <MText
            text={description}
            size={11}
            colour={Colours.DarkGray}
            style={{ lineHeight: '14px' }}
            className="mt-1"
          />
        </div>
      )}
      className="mb-4 align-items-start"
    />
  );

  const handleStatusModalClose = () => {
    if (isUpdatingStatus) {
      return;
    }
    setStatusModalOpen(false);
  };

  return (
    <div className="container-fluid" ref={containerRef} style={{ minHeight: '100dvh' }}>
      <div className="row d-flex flex-column">
        <div className="col-12 col-xl-11 my-3">
          <Paper className="px-4 pt-2 pb-4 mb-5">
            <div className="ml-1 mt-2 mb-3 d-flex flex-row justify-content-between align-items-center">
              <div className="d-flex flex-row align-items-center">
                <IconButton
                  onClick={() => {
                    const listUrl = Url.Albums.replace(':pageId', props.selectedPage.id);
                    props.history.push(listUrl);
                  }}
                  className="mr-2"
                  size="small"
                >
                  <ArrowBack style={{ fontSize: 18, color: Colours.LightGray4 }} />
                </IconButton>
                <MText text="Albums" colour={Colours.LightGray4} size={12} />
              </div>

              <div className="d-flex flex-row align-items-center" style={{ gap: 12 }}>
                <div className="d-flex flex-row align-items-center">
                  <MText text="Status:" size={11} colour={Colours.LightGray4} className="mr-2" />
                  <div
                    className="d-flex flex-row align-items-center"
                    style={{
                      backgroundColor: Colours.White,
                      border:          `1px solid ${Colours.LightGray4}`,
                      color:           Colours.Black,
                      borderRadius:    16,
                      padding:         '4px 14px 4px 10px',
                      fontSize:        11,
                      fontWeight:      '500',
                      cursor:          isReadOnly ? 'not-allowed' : 'pointer',
                      opacity:         isReadOnly ? 0.5 : 1,
                    }}
                    onClick={() => {
                      if (isReadOnly) {
                        return;
                      }
                      setSelectedStatus(album?.status || Status.Draft);
                      setStatusModalOpen(true);
                    }}
                  >
                    <div
                      style={{
                        width:           12,
                        height:          12,
                        borderRadius:    '50%',
                        backgroundColor: album?.status === Status.Live
                          ? Colours.Green
                          : (album?.status === Status.Rejected
                            ? Colours.Red
                            : Colours.LightGray4
                          ),
                        marginRight: 6,
                      }}
                    />
                    {album?.status || Status.Draft}
                  </div>
                </div>

                <IconButton
                  size="small"
                  style={{ border: `1px solid ${Colours.LightGray4}`, borderRadius: 4, padding: '4px 10px' }}
                  onClick={(e) => setMenuAnchorEl(e.currentTarget)}
                >
                  <MenuIcon style={{ fontSize: 16 }} />
                </IconButton>
              </div>
            </div>

            <div className="d-flex flex-row justify-content-between align-items-center mb-3">
              <div className="d-flex flex-column pl-2">
                <div className="d-flex flex-row align-items-center">
                  <MText
                    size={containerHeaderTextStyle.fontSize}
                    bold
                    text={album?.name || 'Album'}
                  />
                  {!isReadOnly && (
                    <IconButton
                      size="small"
                      className="ml-2"
                      style={{ border: `1px solid ${Colours.LightGray4}`, padding: '3px 8px', borderRadius: 16 }}
                      onClick={() => {
                        setEditingAlbum(album);
                        setIsCreateModalOpen(true);
                      }}
                    >
                      <Edit style={{ fontSize: 13 }} />
                    </IconButton>
                  )}
                </div>
                {!IsEmpty(album?.description) && (
                  <MText
                    text={album?.description?.length > 50 ? `${album.description.substring(0, 50)}...` : album.description}
                    colour={Colours.LightGray4}
                    className="mt-1"
                    size={11}
                  />
                )}
              </div>

              {!isReadOnly && (
                <ToolTip title="Add Photos">
                  <MButton
                    disableElevation
                    colour={Colours.Black}
                    style={{
                      color:        Colours.White,
                      borderRadius: 16,
                      minHeight:    30,
                    }}
                    textSize={11}
                    text="Add photos"
                    type="button"
                    className="px-3"
                    onClick={() => {
                      const addPhotosUrl = Url.AlbumPhotosAdd
                        .replace(':pageId', props.selectedPage.id)
                        .replace(':albumId', album.id);
                      props.history.push(addPhotosUrl);
                    }}
                    icon={() => <Add style={{ fontSize: 13, marginRight: 4, color: Colours.White }} />}
                  />
                </ToolTip>
              )}
            </div>

            <div className="row" style={{ rowGap: 24 }}>
              {loadingDetails ? (
                new Array(8).fill(0).map((_, idx) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <div key={`skeleton-${idx}`} className="col-6 col-md-3 px-2">
                    <div style={{ width: '100%', aspectRatio: 1 }}>
                      <Skeleton variant="rect" style={{ width: '100%', height: '100%' }} />
                    </div>
                  </div>
                ))
              ) : (
                album?.photos?.length > 0 ? (
                  album.photos.map((photo) => (
                    <div key={photo.id} className="col-6 col-md-3" style={{ paddingRight: 12, paddingLeft: 12 }}>
                      <div
                        style={{ width: '100%', aspectRatio: 1, overflow: 'hidden', cursor: 'pointer' }}
                        onClick={() => setSelectedPhoto(photo)}
                      >
                        <ProgressiveImage
                          image={imageResizeUrl(photo.display || photo.cropUrl || photo.url, 300, 300)}
                          width="100%"
                          height="100%"
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="col-12 py-5 d-flex justify-content-center">
                    <MText text="No photos in this album" colour={Colours.DarkGray} />
                  </div>
                )
              )}
            </div>
          </Paper>

          {!loadingDetails && album?.photos?.length > 0 && (
            <div
              className="position-fixed py-2 pr-5 d-flex flex-row align-items-center"
              style={{ background: Colours.White, bottom: 5, width: '100%', borderRadius: '10px', zIndex: 99 }}
            >
              <MText className="ml-3" text={`Total count: ${album.photos.length}`} />
            </div>
          )}
        </div>
      </div>

      <CreateEditAlbumModal
        open={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        album={editingAlbum}
        onComplete={() => {
          fetchAlbumDetails(albumIdFromUrl);
        }}
      />

      <PhotoDetailsModal
        open={!IsEmpty(selectedPhoto)}
        photo={selectedPhoto}
        poiCoord={selectedPhoto?.poiCoord}
        albumId={album?.id}
        disabled={isReadOnly}
        onClose={() => setSelectedPhoto(null)}
        onMutationComplete={() => {
          setSelectedPhoto(null);
          if (!IsEmpty(album)) {
            fetchAlbumDetails(album.id);
          }
        }}
      />

      <Dialog open={!IsEmpty(albumToDelete)} fullWidth maxWidth="sm" onClose={() => setAlbumToDelete(null)}>
        <DialogTitle>
          <div className="w-100 d-flex flex-column">
            <MText text="Delete Album" size={15} />
            <Divider className="my-2" />
          </div>
        </DialogTitle>
        <DialogContent>
          <div className="d-flex flex-column pb-3">
            <MText text={`Are you sure you want to delete "${albumToDelete?.name}"?`} semibold size={13} />
            <MText text="All photos inside this album will also be deleted." colour={Colours.Red} size={12} className="mt-2" />
          </div>
          <Divider className="my-3" />
          <div className="pb-3 d-flex flex-row justify-content-between">
            <MButton text="Cancel" variant="outlined" onClick={() => setAlbumToDelete(null)} />
            <MButton text="Delete" colour={Colours.Red} textColour={Colours.White} onClick={handleDeleteAlbum} />
          </div>
        </DialogContent>
      </Dialog>

      <Menu
        anchorEl={menuAnchorEl}
        keepMounted
        open={!IsEmpty(menuAnchorEl)}
        onClose={() => setMenuAnchorEl(null)}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        elevation={2}
      >
        <MenuItem
          onClick={() => {
            setMenuAnchorEl(null);
            setAlbumToDelete(album);
          }}
          className="px-4"
        >
          <DeleteOutline style={{ fontSize: 16, marginRight: 8 }} />
          <MText text="Delete Album" size={13} />
        </MenuItem>
      </Menu>

      <Dialog open={statusModalOpen} onClose={handleStatusModalClose} fullWidth maxWidth="xs" disableEscapeKeyDown={isUpdatingStatus}>
        <DialogTitle>
          <div className="w-100 d-flex justify-content-between align-items-center">
            <MText text="Change Album's Status" bold size={13} />
            <IconButton onClick={handleStatusModalClose} size="small" style={{ padding: 4 }} disabled={isUpdatingStatus}>
              <Icons.Cross color={Colours.Black} fontSize={20} />
            </IconButton>
          </div>
        </DialogTitle>
        <DialogContent className="px-4 pb-4 pt-2">
          <RadioGroup value={selectedStatus} onChange={(e) => setSelectedStatus(e.target.value)}>
            {renderStatusOption(Status.Draft, 'Draft', 'Album and all photos under it will not be visible to the public')}
            {renderStatusOption(Status.Live, 'Live', 'Album and all photos under it will be visible to the public')}
          </RadioGroup>

          <MButton
            text={isUpdatingStatus ? 'Updating...' : 'Update'}
            className="w-100 mt-3"
            style={{ borderRadius: 8, padding: '10px 0', color: Colours.White }}
            colour={(isUpdatingStatus || selectedStatus === album?.status) ? Colours.Gray : Colours.Secondary}
            textColour={Colours.White}
            onClick={onUpdateStatus}
            disabled={
              isUpdatingStatus
              || selectedStatus === album?.status
            }
          />
        </DialogContent>
      </Dialog>

    </div>
  );
};

AlbumDetails.propTypes = {
  selectedPage: PropTypes.shape({
    id:   PropTypes.number.isRequired,
    name: PropTypes.string,
    logo: PropTypes.string,
  }).isRequired,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      albumId: PropTypes.string,
    }),
  }).isRequired,
};

const mapStateToProps = (state) => ({
  selectedPage: state.page.selected,
});

export default connect(mapStateToProps, null)(AlbumDetails);
